/**
 * Soundtrack and sound effects for the inference animation, synthesized live
 * with Web Audio: no audio files.
 *
 * Every sound is keyed to timeline time, the clock the picture runs on. Sounds
 * are queued a moment ahead at `(t - now) / speed`, so slow motion halves the
 * tempo, and they go through a disposable bus: a seek, a pause or a speed
 * change fades that bus out and queues again from the new moment, so skipped
 * time never plays.
 *
 * The sound follows the same visual language as the picture:
 * - heavy math is a chatter of computer bleeps that thickens with the COMPUTE gauge
 * - memory traffic is a ticking stream, one tick per packet
 * - compression is a riser, a vacuum swell, then an impact
 * Each token also has its own note, so a batch moving between boxes plays a
 * run up the scale, and a decode step replays every cached token: the growing
 * KV cache is audible.
 */
import { clamp, easeInOutCubic, hash, lerp } from "./fx";
import {
  attentionWeight,
  loadAt,
  PROMPT,
  PROMPT_COUNT,
  ROUTES,
  ST_EMBED,
  ST_L1,
  ST_LM,
  ST_WARP,
  TOKENS,
  type ModelKind,
  type Pass,
  type Timeline
} from "./timeline";

/** How far ahead of the picture sounds are queued, in real seconds. */
const LOOKAHEAD = 0.12;
const MASTER = 0.62;
/** Roughly 125 BPM. The exact tempo is fitted to the animation (see `buildGrid`). */
const NOMINAL_BAR = (60 / 125) * 4;
/** How long after a banner appears it slams into place. */
const BANNER_HIT = 0.12;

const mod = (n: number, m: number) => ((n % m) + m) % m;

const hz = (midi: number) => 440 * Math.pow(2, (midi - 69) / 12);
// The volume ticks climb A minor pentatonic and land on A at full volume.
const VOLUME_NOTES = [60, 62, 64, 67, 69, 72, 74, 76, 79, 81];

// Every token has its own note in A minor pentatonic. The prompt climbs the scale; the answer lands on the tonic.
const TOKEN_NOTES = [69, 72, 74, 76, 79, 81, 84, 81, 84, 88, 93];
const note = (token: number) => TOKEN_NOTES[token] ?? 81;
// The eight experts play the scale too, so routing sounds like a chord being picked.
const EXPERT_NOTES = [64, 67, 69, 72, 74, 76, 79, 81];
// Heavy math: bleeps drawn from the same scale, an octave up.
const CHATTER = [76, 79, 81, 84, 86, 88, 91, 93];

// The ending resolves to A major: stage clear.
const OUTRO_CHORD = [57, 61, 64, 69, 73];

// Am7, Fmaj7, G, E7: one chord per bar. The E major bar pushes every phrase back to Am.
const CHORDS = [
  { root: 45, notes: [57, 60, 64, 67] },
  { root: 41, notes: [53, 57, 60, 64] },
  { root: 43, notes: [55, 59, 62, 67] },
  { root: 40, notes: [56, 59, 62, 64] }
];
// Chords for the bars that lead into a phrase, by how many there are. They turn around to E, which pulls into the Am
// the hook starts on, and they open on Am so a smash still resolves.
const PICKUP_CHORDS: Record<number, number[]> = { 1: [0], 2: [0, 3], 3: [0, 1, 3] };

type Phrase = Array<Array<[number, number, number]>>;
// Lead hooks, one bar per chord: [sixteenth, note, length in sixteenths]. A plays over prefill, B (higher) over decode.
const HOOKS: [Phrase, Phrase] = [
  [
    [
      [0, 76, 2],
      [3, 76, 2],
      [6, 74, 2],
      [8, 72, 2],
      [10, 74, 2],
      [12, 76, 4]
    ],
    [
      [0, 72, 2],
      [3, 72, 2],
      [6, 69, 2],
      [8, 72, 3],
      [11, 74, 2],
      [13, 76, 3]
    ],
    [
      [0, 79, 3],
      [3, 76, 2],
      [6, 74, 2],
      [8, 71, 2],
      [10, 74, 2],
      [12, 79, 4]
    ],
    [
      [0, 80, 4],
      [4, 76, 2],
      [6, 80, 2],
      [8, 83, 4],
      [12, 81, 2],
      [14, 80, 2]
    ]
  ],
  [
    [
      [0, 81, 2],
      [2, 79, 2],
      [4, 76, 2],
      [6, 79, 2],
      [8, 81, 4],
      [12, 84, 4]
    ],
    [
      [0, 84, 2],
      [2, 81, 2],
      [4, 77, 2],
      [6, 81, 2],
      [8, 84, 6],
      [14, 81, 2]
    ],
    [
      [0, 83, 2],
      [2, 79, 2],
      [4, 74, 2],
      [6, 79, 2],
      [8, 83, 4],
      [12, 86, 4]
    ],
    [
      [0, 83, 3],
      [3, 80, 3],
      [6, 76, 2],
      [8, 80, 4],
      [12, 83, 4]
    ]
  ]
];

// A syncopated bassline in the Streets of Rage mold: [sixteenth, interval above the root, length in sixteenths].
const BASS_GROOVE: Array<[number, number, number]> = [
  [0, 0, 2],
  [2, 0, 1],
  [3, 12, 1],
  [5, 0, 1],
  [6, 7, 1],
  [8, 0, 2],
  [10, 12, 1],
  [11, 0, 1],
  [13, 7, 1],
  [14, 12, 2]
];
const STAB_STEPS = [3, 6, 10, 13];
const ARP_ORDER = [0, 1, 2, 3, 4, 3, 2, 1];

type SectionKind = "boot" | "tokenize" | "embed" | "inside" | "run" | "build" | "afterglow" | "decode" | "outro";
type Section = { kind: SectionKind; start: number; end: number };
type Cue = { t: number; kind: string; play: (at: number) => void };
type Bus = "music" | "pumped" | "drums" | "sfx";
type Epoch = Record<Bus | "verb" | "echo", GainNode>;
export type SoundUi = "coin" | "select" | "hover" | "slow" | "fast";

/** What plays in each section. */
type Part = {
  pad?: number;
  kick?: "four" | "break" | "build";
  clap?: boolean;
  hats?: "eighths" | "sixteenths" | "house";
  hatVel?: number;
  /** Delay of the off sixteenths, as a fraction of a sixteenth. */
  swing?: number;
  bass?: "pulse" | "pump" | "gallop" | "groove" | "pedal";
  stabs?: boolean;
  arp?: number;
  lead?: 0 | 1;
  crash?: boolean;
  fills?: boolean;
};

// Quiet tension while the prompt is typed, then the drop at PREFILL, which every later section builds on.
const ARRANGEMENT: Record<SectionKind, Part> = {
  boot: { pad: 0.02, bass: "pulse" },
  tokenize: { pad: 0.022, bass: "pulse", hats: "sixteenths", hatVel: 0.5, arp: 0.4 },
  embed: { pad: 0.026, kick: "four", clap: true, hats: "eighths", bass: "pump", arp: 0.75, crash: true, fills: true },
  inside: { pad: 0.026, kick: "four", clap: true, hats: "sixteenths", bass: "gallop", arp: 0.65, fills: true },
  run: {
    pad: 0.03,
    kick: "four",
    clap: true,
    hats: "house",
    bass: "groove",
    stabs: true,
    arp: 0.5,
    lead: 0,
    crash: true,
    fills: true
  },
  build: { pad: 0.04, kick: "build", hats: "sixteenths", bass: "pedal", arp: 0.55 },
  afterglow: { pad: 0.04 },
  decode: {
    pad: 0.026,
    kick: "break",
    clap: true,
    hats: "sixteenths",
    swing: 0.14,
    bass: "groove",
    stabs: true,
    arp: 0.4,
    lead: 1,
    crash: true,
    fills: true
  },
  outro: {}
};

export class Sound {
  private ctx: BaseAudioContext | null = null;
  private master: GainNode;
  private musicLevel: GainNode;
  private musicTone: BiquadFilterNode;
  private duck: GainNode;
  private pump: GainNode;
  private buses: Record<Bus, GainNode>;
  private verbIn: GainNode;
  private echoIn: GainNode;
  private echoDelay: DelayNode;
  private verbOut: GainNode;
  private noise: AudioBuffer;
  private pulse: PeriodicWave;
  private crunch: WaveShaperNode["curve"];
  private epoch: Epoch | null = null;
  /** The pan and send nodes behind each voice's output, so they can be disconnected with it. */
  private chains = new WeakMap<AudioNode, AudioNode[]>();

  private cues: Cue[] = [];
  private sections: Section[] = [];
  // One beat grid for the whole soundtrack, so the groove never skips when a section changes.
  /** Timeline time of bar 0's downbeat: the PREFILL drop. */
  private origin = 0;
  private stepLen = NOMINAL_BAR / 16;
  private barLen = NOMINAL_BAR;
  /** Bars where the chord progression starts over on Am. */
  private anchors: number[] = [];
  /** Bars that lead into a phrase, as [index, count]. The hook sits them out, so it only ever plays whole. */
  private pickups = new Map<number, [number, number]>();
  private decodeStart = 0;
  private outroStart = 0;
  private model: ModelKind;
  private enabled = true;
  /** 0 to 1, as the listener set it. */
  private volume = 1;
  private running = false;
  private speed = 1;
  /** Queued up to here, in timeline seconds. */
  private cursor = 0;
  /** Timeline time `t0` sounds at audio time `a0`. */
  private t0 = 0;
  private a0 = 0;
  private anchoredAt = 0;
  private lastScrub = 0;
  private idleSince = 0;
  private paused = false;
  /** The lead's previous note, so the next one can glide from it. */
  private lastLead: { midi: number; end: number } | null = null;

  /** Dev only: called for every cue that gets queued. */
  trace: ((kind: string, t: number, at: number) => void) | null = null;

  constructor(
    private tl: Timeline,
    model: ModelKind
  ) {
    this.model = model;
    this.buildGrid();
    this.buildCues();
  }

  // ---------------------------------------------------------------------------
  // Public controls

  /** Creates or resumes the audio context. Call from a click or key press, which browsers require. */
  unlock() {
    if (!this.ctx) {
      const AC =
        window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AC) return;
      this.attach(new AC({ latencyHint: "interactive" }));
    }
    this.wake();
  }

  setEnabled(on: boolean) {
    this.enabled = on;
    this.applyLevel();
    if (this.ctx && !on) this.halt(false);
  }

  setVolume(volume: number) {
    this.volume = clamp(volume);
    this.applyLevel();
  }

  /** A chiptune blip whose pitch climbs with the volume, played once the new level has taken hold. */
  volumeTick(volume: number) {
    if (!this.ctx || !this.enabled) return;
    this.wake();
    const at = this.ctx.currentTime + 0.05;
    const note = VOLUME_NOTES[Math.round(clamp(volume) * (VOLUME_NOTES.length - 1))];
    this.blip(at, hz(note), 0.05, { type: "pulse", vol: 0.04 });
    this.blip(at + 0.035, hz(note + 12), 0.06, { type: "pulse", vol: 0.02 });
  }

  /** Ears don't hear gain linearly, so the slider's position is squared: halfway is a quarter of the gain, 12 dB down. */
  private level() {
    return this.enabled ? MASTER * this.volume ** 2 : 0;
  }

  private applyLevel() {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setTargetAtTime(this.level(), now, 0.03);
  }

  setModel(model: ModelKind) {
    if (model === this.model) return;
    this.model = model;
    this.buildCues();
  }

  /** A jump in the timeline: drop everything queued and pick up from `t`. */
  seek(t: number) {
    if (!this.ctx || !this.enabled) return;
    if (this.running) {
      this.renew();
      this.anchor(t);
      this.cursor = t - 1e-6;
      this.sustain(t);
    } else this.cursor = t - 1e-6;
    this.scrub(t);
  }

  /** Call once per frame with the picture's clock. */
  update(t: number, playing: boolean, speed: number) {
    const ctx = this.ctx;
    if (!ctx || !this.enabled) return;
    const now = ctx.currentTime;
    if (!playing) {
      // A natural end lets the last chord ring; a pause cuts it.
      if (this.running) this.halt(t < this.tl.duration - 1e-3);
      if (ctx.state === "running" && performance.now() - this.idleSince > 4000) (ctx as AudioContext).suspend?.();
      return;
    }
    this.idleSince = performance.now();
    if (!this.running || speed !== this.speed) {
      const resumed = this.running;
      this.speed = speed;
      this.renew();
      this.anchor(t);
      this.cursor = t - 1e-6;
      this.sustain(t);
      if (!resumed && this.paused) this.powerUp(now);
      this.paused = false;
      this.running = true;
      this.setSlowMotion(speed < 1);
    } else if (Math.abs(this.t0 + (now - this.anchoredAt) * speed - t) > 0.06) {
      // The picture and the audio clock drifted apart (a slow frame, a hidden tab): re-align, keep the queue.
      this.anchor(t);
    }
    if (t - this.cursor > 0.3) this.cursor = t;
    const horizon = t + LOOKAHEAD * speed;
    if (horizon > this.cursor) {
      this.schedule(this.cursor, horizon);
      this.cursor = horizon;
    }
    this.mixAt(t, now, true);
  }

  /** The picture stopped drawing (scrolled away, hidden tab): stop quietly, and pick up again on the next update. */
  hush() {
    if (!this.ctx || !this.running) return;
    this.renew();
    this.halt(false);
  }

  /** Sounds for the controls, which play right away rather than on the timeline. */
  ui(kind: SoundUi, model?: ModelKind) {
    if (!this.ctx || !this.enabled) return;
    this.wake();
    const at = this.ctx.currentTime + 0.005;
    if (kind === "coin") this.coin(at);
    else if (kind === "select") this.select(at, model ?? this.model);
    else if (kind === "hover") this.blip(at, 2300, 0.02, { type: "sine", vol: 0.025, dest: this.buses.sfx });
    else if (kind === "slow") this.bulletTime(at, true);
    else this.bulletTime(at, false);
  }

  dispose() {
    if (this.ctx instanceof AudioContext) this.ctx.close();
    this.ctx = null;
  }

  /** Renders a stretch of the soundtrack offline, for tests and previews. */
  static async render(tl: Timeline, model: ModelKind, from: number, to: number, speed = 1): Promise<AudioBuffer> {
    const rate = 44100;
    const length = ((to - from) / speed + 2.5) * rate;
    const ctx = new OfflineAudioContext(2, Math.ceil(length), rate);
    const s = new Sound(tl, model);
    s.attach(ctx);
    s.speed = speed;
    s.setSlowMotion(speed < 1);
    s.t0 = from;
    s.a0 = 0.05;
    s.sustain(from);
    // Queue a little at a time, as live playback does: a graph holding the whole timeline renders slowly.
    let cursor = from - 1e-6;
    const advance = (audioTime: number) => {
      const t = Math.min(to, from + (audioTime - s.a0) * speed);
      const horizon = Math.min(to, t + 0.5 * speed);
      if (horizon > cursor) s.schedule(cursor, horizon);
      cursor = Math.max(cursor, horizon);
      for (let bt = t; bt < Math.min(to, t + 0.25 * speed); bt += 1 / 60) s.mixAt(bt, s.at(bt), false);
    };
    advance(0);
    const quantum = 128 / rate;
    for (let a = 0.25; a < length / rate - 0.5; a += 0.25) {
      const at = Math.round(a / quantum) * quantum;
      ctx.suspend(at).then(() => {
        advance(at);
        ctx.resume();
      });
    }
    return ctx.startRendering();
  }

  // ---------------------------------------------------------------------------
  // Graph

  private attach(ctx: BaseAudioContext) {
    this.ctx = ctx;
    this.noise = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const d = this.noise.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;

    // A 25% pulse: the classic handheld-console square.
    const n = 32;
    const real = new Float32Array(n);
    const imag = new Float32Array(n);
    for (let k = 1; k < n; k++) imag[k] = (2 / (k * Math.PI)) * Math.sin(k * Math.PI * 0.25) * 1.2;
    this.pulse = ctx.createPeriodicWave(real, imag);

    const curve = new Float32Array(1024);
    for (let i = 0; i < curve.length; i++) {
      const x = (i / (curve.length - 1)) * 2 - 1;
      curve[i] = Math.tanh(x * 6) * 0.8;
    }
    this.crunch = curve;

    // A safety limiter only: the mix keeps its dynamics, so the smashes land harder than the ticks.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -2;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.001;
    limiter.release.value = 0.1;
    this.master = this.gainNode(this.level());
    this.master.connect(limiter).connect(ctx.destination);

    this.musicLevel = this.gainNode(1);
    this.musicTone = this.filterNode("lowpass", 18000, 0.5);
    this.duck = this.gainNode(1);
    this.musicTone.connect(this.musicLevel).connect(this.duck).connect(this.master);
    this.pump = this.gainNode(1);
    this.pump.connect(this.musicTone);
    this.buses = {
      music: this.gainNode(0.75),
      pumped: this.gainNode(0.8),
      drums: this.gainNode(0.75),
      sfx: this.gainNode(1)
    };
    this.buses.music.connect(this.musicTone);
    this.buses.drums.connect(this.musicTone);
    this.buses.pumped.connect(this.pump);
    this.buses.sfx.connect(this.master);

    // Reverb: a generated impulse, decorrelated per channel for width.
    const ir = ctx.createBuffer(2, Math.floor(ctx.sampleRate * 2.4), ctx.sampleRate);
    for (let c = 0; c < 2; c++) {
      const ch = ir.getChannelData(c);
      let lp = 0;
      for (let i = 0; i < ch.length; i++) {
        lp = lp * 0.55 + (Math.random() * 2 - 1) * 0.45;
        ch[i] = lp * Math.pow(1 - i / ch.length, 3.2);
      }
    }
    const verb = ctx.createConvolver();
    verb.buffer = ir;
    this.verbIn = this.gainNode(1);
    this.verbOut = this.gainNode(0.55);
    this.verbIn.connect(verb).connect(this.verbOut).connect(this.master);

    // Echo: a dotted eighth that follows the playback speed.
    this.echoIn = this.gainNode(1);
    this.echoDelay = ctx.createDelay(2);
    this.echoDelay.delayTime.value = this.stepLen * 3;
    const fb = this.gainNode(0.34);
    const echoTone = this.filterNode("lowpass", 3200, 0.5);
    const echoOut = this.gainNode(0.45);
    this.echoIn.connect(this.echoDelay);
    this.echoDelay.connect(echoTone).connect(fb).connect(this.echoDelay);
    this.echoDelay.connect(echoOut).connect(this.master);
    echoOut.connect(this.verbIn);

    this.renew();
  }

  /** Swaps in a fresh bus for queued sounds and fades out the old one. */
  private renew() {
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const old = this.epoch;
    if (old) {
      for (const g of Object.values(old)) {
        g.gain.cancelScheduledValues(now);
        g.gain.setTargetAtTime(0, now, 0.012);
      }
      if (ctx instanceof AudioContext) setTimeout(() => Object.values(old).forEach((g) => g.disconnect()), 400);
    }
    const make = (to: AudioNode) => {
      const g = this.gainNode(1);
      g.connect(to);
      return g;
    };
    this.epoch = {
      music: make(this.buses.music),
      pumped: make(this.buses.pumped),
      drums: make(this.buses.drums),
      sfx: make(this.buses.sfx),
      verb: make(this.verbIn),
      echo: make(this.echoIn)
    };
    for (const p of [this.pump.gain, this.duck.gain]) {
      p.cancelScheduledValues(now);
      p.setTargetAtTime(1, now, 0.02);
    }
    this.lastLead = null;
  }

  private halt(paused: boolean) {
    this.running = false;
    this.paused = paused;
    this.idleSince = performance.now();
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    if (paused) {
      this.renew();
      this.powerDown(now);
    }
  }

  private wake() {
    const ctx = this.ctx;
    if (ctx instanceof AudioContext && ctx.state === "suspended") ctx.resume().catch(() => undefined);
    this.idleSince = performance.now();
  }

  private anchor(t: number) {
    const ctx = this.ctx;
    const now = ctx.currentTime;
    // Queue slightly early to cover the device's output latency, so sound and picture land together.
    const latency = ctx instanceof AudioContext ? clamp(ctx.outputLatency || ctx.baseLatency || 0, 0, 0.1) : 0;
    this.t0 = t;
    this.a0 = now + 0.01 - latency;
    this.anchoredAt = now;
  }

  /** Audio time at which timeline time `t` should be heard. Never negative: Web Audio rejects that. */
  private at(t: number) {
    return Math.max(0, this.a0 + (t - this.t0) / this.speed);
  }

  /** Timeline seconds to real seconds. */
  private real(dur: number) {
    return dur / this.speed;
  }

  private setSlowMotion(slow: boolean) {
    const ctx = this.ctx;
    const now = ctx.currentTime;
    this.echoDelay.delayTime.setTargetAtTime(this.real(this.stepLen * 3), now, 0.05);
    this.verbOut.gain.setTargetAtTime(slow ? 0.8 : 0.55, now, 0.1);
  }

  // ---------------------------------------------------------------------------
  // Scheduling

  private schedule(from: number, to: number) {
    // Cues in (from, to]
    let lo = 0;
    let hi = this.cues.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (this.cues[mid].t <= from) lo = mid + 1;
      else hi = mid;
    }
    for (let i = lo; i < this.cues.length && this.cues[i].t <= to; i++) {
      const cue = this.cues[i];
      const at = this.at(cue.t);
      cue.play(at);
      this.trace?.(cue.kind, cue.t, at);
    }
    this.scheduleMusic(from, to);
  }

  private scheduleMusic(from: number, to: number) {
    for (let k = Math.floor((from - this.origin) / this.stepLen) + 1; ; k++) {
      const t = this.origin + k * this.stepLen;
      if (t > to) break;
      if (t >= 0) this.step(k, t);
    }
  }

  private sectionAt(t: number) {
    return this.sections.find((sec) => t >= sec.start && t < sec.end);
  }

  /** The chord for a bar: the progression restarts on Am at each anchor, and builds sit on E, the chord that pulls back to Am. */
  private chordAt(sec: Section, bar: number) {
    if (sec.kind === "build") return CHORDS[3];
    const pickup = this.pickups.get(bar);
    if (pickup) return CHORDS[PICKUP_CHORDS[pickup[1]][pickup[0]]];
    return CHORDS[this.phraseBar(bar)];
  }

  private phraseBar(bar: number) {
    const anchor = this.anchors.filter((a) => a <= bar).pop() ?? 0;
    return mod(bar - anchor, 4);
  }

  /** After a seek or resume, bring back the pad that would be ringing at `t`. */
  private sustain(t: number) {
    const sec = this.sectionAt(t);
    const level = sec && ARRANGEMENT[sec.kind].pad;
    if (!level) return;
    const bar = Math.floor((t - this.origin) / this.barLen + 1e-9);
    const barStart = this.origin + bar * this.barLen;
    // Right on a bar line, the sequencer plays this pad itself.
    if (t - barStart < 1e-5) return;
    const end = Math.min(sec.end, barStart + this.barLen);
    if (end - t > 0.25) this.pad(this.at(t), this.chordAt(sec, bar).notes, this.real(end - t), level, 0.15);
  }

  /**
   * Fits the tempo to the animation: bar 0 lands on the PREFILL drop, and the prefill smash lands exactly on a
   * downbeat. Section changes then snap to the nearest beat instead of restarting the music, and the moments where
   * the tune hands over (a build, the ending) wait for a phrase to finish.
   */
  private buildGrid() {
    const tl = this.tl;
    const prefill = tl.passes[0];
    const I = prefill.interior;
    this.origin = tl.prefillBanner + BANNER_HIT;
    const span = prefill.smash - this.origin;
    this.barLen = span / Math.max(1, Math.round(span / NOMINAL_BAR));
    this.stepLen = this.barLen / 16;
    const beat = this.stepLen * 4;
    const nearestBeat = (t: number) => this.origin + Math.round((t - this.origin) / beat) * beat;
    const nextBeat = (t: number) => this.origin + Math.ceil((t - this.origin) / beat - 1e-9) * beat;
    const nearestBar = (t: number) => this.origin + Math.round((t - this.origin) / this.barLen) * this.barLen;
    const barOf = (t: number) => Math.round((t - this.origin) / this.barLen);
    const barTime = (bar: number) => this.origin + bar * this.barLen;
    const onBar = (t: number) => Math.abs(t - nearestBar(t)) < 0.06;

    // Decode comes in on a downbeat when one is close to its banner.
    const bar = nearestBar(tl.decodeBanner);
    this.decodeStart = Math.abs(bar - tl.decodeBanner) < 0.4 && bar > prefill.smash ? bar : nextBeat(tl.decodeBanner);
    // The ending waits for the next downbeat after the end card appears.
    const outroBar = Math.ceil((tl.endCard - this.origin) / this.barLen - 1e-9);
    this.outroStart = barTime(outroBar);

    // Each smash gets a one-bar build, so the tune plays out its phrase right up to it.
    const smashBar = barOf(prefill.smash);
    const marks: Array<[number, SectionKind]> = [
      [-Infinity, "boot"],
      [nearestBeat(tl.tokenizeStart), "tokenize"],
      [this.origin, "embed"],
      [nearestBeat(I.Z), "inside"],
      [nearestBeat(I.zoomOut[0]), "run"],
      [barTime(smashBar - 1), "build"],
      [prefill.smash, "afterglow"],
      [this.decodeStart, "decode"]
    ];
    this.anchors = [0, smashBar, barOf(this.decodeStart)];
    const handoffs = [smashBar - 1, outroBar];
    // A decode smash that lands on a downbeat gets its own build; the others just land on top of the groove.
    for (const p of tl.passes.slice(1)) {
      const b = barOf(p.smash);
      if (!onBar(p.smash) || barTime(b - 1) <= this.decodeStart) continue;
      marks.push([barTime(b - 1), "build"], [barTime(b), "decode"]);
      this.anchors.push(b);
      handoffs.push(b - 1);
    }
    // Fit the phrases so the last one before each handoff ends right at it. When the stretch isn't a whole number of
    // four-bar phrases, the spare bars go first as a pickup: groove and chords without the hook, which then comes in
    // once and plays whole. Starting the hook in them instead cut it off after a bar or two and started it over.
    const starts = [...this.anchors];
    this.pickups = new Map();
    for (const h of handoffs) {
      const from = Math.max(...starts.filter((a) => a < h));
      const spare = mod(h - from, 4);
      if (!spare) continue;
      for (let i = 0; i < spare; i++) this.pickups.set(from + i, [i, spare]);
      this.anchors.push(from + spare);
    }
    this.anchors.sort((x, y) => x - y);
    marks.push([this.outroStart, "outro"]);
    marks.sort((x, y) => x[0] - y[0]);
    this.sections = marks.map(([start, kind], i) => ({
      kind,
      start,
      end: i + 1 < marks.length ? marks[i + 1][0] : Infinity
    }));
  }

  /** One sixteenth of music, `k` counted on the global grid. */
  private step(k: number, t: number) {
    const sec = this.sectionAt(t);
    if (!sec || sec.kind === "outro") return;
    const at = this.at(t);
    this.trace?.(`step:${sec.kind}`, t, at);
    const part = ARRANGEMENT[sec.kind];
    const S = this.stepLen;
    const s = mod(k, 16);
    const bar = Math.floor(k / 16);
    const phrase = this.phraseBar(bar);
    const pickup = this.pickups.get(bar);
    const chord = this.chordAt(sec, bar);
    const barStart = this.origin + bar * this.barLen;
    const barEnd = Math.min(sec.end, barStart + this.barLen);
    const progress = clamp((t - sec.start) / Math.max(0.1, sec.end - sec.start));
    // A build leaves a beat of silence before its smash.
    const gap = sec.kind === "build" && sec.end - t < S * 1.5;
    const swung = s % 2 && part.swing ? this.at(t + S * part.swing) : at;
    const pan = k % 2 ? 0.35 : -0.35;
    // A tom fill closes every phrase and every pickup, when that bar fits in the section.
    const last = pickup ? pickup[0] === pickup[1] - 1 : phrase === 3;
    const fill = part.fills && last && s >= 12 && barStart + this.barLen <= sec.end + 1e-6;

    // A pad per bar, and one from the moment a section comes in mid-bar.
    if (part.pad && (s === 0 || Math.abs(t - sec.start) < 1e-6))
      this.pad(at, chord.notes, this.real(barEnd - t), part.pad);
    if (part.crash && s === 0 && phrase === 0 && !pickup) this.crash(at, 0.8);

    // Drums
    if (part.kick === "four" && s % 4 === 0) this.kick(at, s === 0 ? 1 : 0.9);
    if (part.kick === "break" && [0, 7, 10].includes(s)) this.kick(at, s === 0 ? 1 : 0.85);
    if (part.kick === "build" && !gap) {
      // Quarters, then eighths, then sixteenths: the kick rolls into the smash.
      const every = progress < 0.45 ? 4 : progress < 0.8 ? 2 : 1;
      if (s % every === 0) this.kick(at, 0.55 + 0.45 * progress);
      // The snare rolls with it: backbeat, eighths, then sixteenths, getting louder.
      const roll = progress < 0.4 ? 8 : progress < 0.7 ? 2 : 1;
      if (s % roll === (roll === 8 ? 4 : 0)) this.snare(at, 0.2 + 0.6 * progress);
    }
    if (part.clap && (s === 4 || s === 12) && !fill) {
      this.clap(at, 1);
      this.snare(at, 0.45);
    }
    const hv = part.hatVel ?? 1;
    if (fill) this.tom(at, 15 - s);
    else if (part.hats === "eighths" && s % 2 === 0) this.hat(at, (s % 4 === 2 ? 0.8 : 0.45) * hv);
    else if (part.hats === "sixteenths" && !gap) this.hat(swung, (s % 4 === 2 ? 0.9 : s % 2 ? 0.35 : 0.55) * hv);
    else if (part.hats === "house") {
      if (s % 4 === 2) this.hat(at, 0.8 * hv, true);
      else this.hat(swung, (s % 2 ? 0.35 : 0.5) * hv);
    }

    // Bass
    const root = chord.root;
    if (part.bass === "pulse" && s % 2 === 0) this.bass(at, root, this.real(S * 1.5), 0.35);
    if (part.bass === "pump" && s % 2 === 0) this.bass(at, root, this.real(S * 1.6), 0.7);
    if (part.bass === "gallop")
      this.bass(at, root + (s % 4 === 2 ? 12 : 0), this.real(S * 0.8), s % 4 === 0 ? 1 : 0.75);
    if (part.bass === "pedal" && s % 2 === 0 && !gap) this.bass(at, root, this.real(S * 1.5), 0.4 + 0.6 * progress);
    if (part.bass === "groove")
      for (const [st, interval, len] of BASS_GROOVE) {
        if (st !== s) continue;
        const dur = this.real(S * len * 0.9);
        this.bass(swung, root + interval, dur, st % 8 === 0 ? 1 : 0.8);
        this.fmBass(swung, root + interval + 12, dur, st % 8 === 0 ? 1 : 0.75);
      }

    // Chords and melody
    if (part.stabs && STAB_STEPS.includes(s) && !fill) this.stab(swung, chord.notes, s === 13 ? 0.75 : 1, pan);
    if (part.arp && !gap) {
      // In a pickup the arp steps forward, so the bars without the hook still have a tune on top.
      const level = pickup ? Math.max(part.arp, 0.6) : part.arp;
      const tones = [...chord.notes.map((m) => m + 12), chord.notes[0] + 24];
      // The filter opens and closes over two bars.
      const sweep = 0.5 - 0.5 * Math.cos((2 * Math.PI * k) / 32);
      this.arp(at, tones[ARP_ORDER[mod(k, 8)]], level * (s % 4 === 0 ? 1 : 0.75), pan, 0.28, sweep);
    }
    if (part.lead !== undefined && !pickup) {
      // The phrase's last note holds through a build that follows and resolves on the smash.
      const intoBuild =
        Math.abs(sec.end - (barStart + this.barLen)) < 1e-6 && this.sectionAt(sec.end)?.kind === "build";
      for (const [st, m, len] of HOOKS[part.lead][phrase]) {
        if (st !== s) continue;
        this.lead(at, m, this.real(S * (len + (intoBuild && st + len >= 16 ? 14 : 0))));
      }
    }

    this.chatter(t);
  }

  /** Heavy math: computer bleeps in key, denser and louder as the compute gauge climbs. Silent while decode waits on memory. */
  private chatter(t: number) {
    for (const half of [0, 0.5]) {
      const tt = t + half * this.stepLen;
      const c = loadAt(this.tl, tt)[0];
      if (c < 0.25 || hash(tt, 1.7) > 0.2 + 0.7 * c) continue;
      const m = CHATTER[Math.floor(hash(tt, 4.1) * CHATTER.length)];
      this.blip(this.at(tt), hz(m), 0.045, {
        type: "triangle",
        vol: 0.012 + 0.024 * c,
        pan: hash(tt, 8.3) * 1.2 - 0.6,
        echo: 0.15
      });
    }
  }

  // ---------------------------------------------------------------------------
  // The cue sheet: every sound effect, at its moment in the timeline

  private buildCues() {
    const tl = this.tl;
    const model = this.model;
    const cues: Cue[] = [];
    const add = (t: number, kind: string, play: (at: number) => void) => cues.push({ t, kind, play });
    const stationPan = (s: number) => -0.7 + s * 0.2;

    // Typing
    for (let k = 0; k < PROMPT.length; k++)
      add(tl.typeStart + (k + 1) / 14, "type", (at) => this.keyClick(at, PROMPT[k] === " ", -0.7 + k * 0.05));

    // Tokenizer: the scan beam, a snip at each cut, the pieces popping apart, the IDs rolling in
    add(tl.tokenizeStart, "scan", (at) => this.laser(at, this.real(0.7)));
    const charW = 12;
    const beamX = (p: number) => lerp(30, 40 + PROMPT.length * charW + 10, easeInOutCubic(p));
    let chars = 0;
    for (let i = 0; i < PROMPT_COUNT; i++) {
      if (i > 0) {
        const x = 40 + chars * charW;
        let lo = 0;
        let hi = 1;
        for (let n = 0; n < 20; n++) {
          const mid = (lo + hi) / 2;
          if (beamX(mid) < x) lo = mid;
          else hi = mid;
        }
        const pan = -0.7 + chars * 0.05;
        add(tl.tokenizeStart + lo * 0.7, "cut", (at) => this.snip(at, pan));
      }
      chars += TOKENS[i].text.length;
      add(tl.splitStart + i * 0.08, "split", (at) => this.bubble(at, note(i), -0.7 + i * 0.2));
    }
    const lastSettle = tl.idStart + (PROMPT_COUNT - 1) * 0.07 + 0.2 + 4 * 0.08;
    for (let t = tl.idStart; t < lastSettle; t += 1 / 30)
      add(t, "id-roll", (at) => this.blip(at, 1600 + hash(t) * 1400, 0.012, { vol: 0.02, type: "square" }));
    for (let i = 0; i < PROMPT_COUNT; i++) {
      const digits = String(TOKENS[i].id).length;
      add(tl.idStart + i * 0.07 + 0.2 + (digits - 1) * 0.08, "id", (at) => this.ding(at, note(i), -0.7 + i * 0.2));
    }

    // Stingers slam in on the beat that brings the drums in.
    add(this.origin - BANNER_HIT, "banner", (at) => this.stinger(at, "prefill"));
    add(this.decodeStart - BANNER_HIT, "banner", (at) => this.stinger(at, "decode"));

    for (const pass of tl.passes) {
      const s = 1 / pass.speed;
      const n = pass.tokens.length;
      const pt = pass.pipeTime;
      const I = pass.interior;
      if (pass.speed > 1) add(pass.dropStart[0] - 0.03, "ff", (at) => this.fastForward(at));

      // Tokens drop from the context into the embedding table and fetch their rows
      pass.tokens.forEach((token, k) => {
        add(pass.dropStart[k], "drop", (at) => this.drop(at, note(token), this.real(pass.dropDur)));
        add(pass.runs[0].lookups[k].at + pt, "lookup", (at) => this.lookup(at, note(token)));
      });

      // Moving between boxes: one swoosh per hop and a plink per token as it lands
      let prevLeave = pass.runs[0].leave;
      for (const run of pass.runs.slice(1)) {
        const lm = run.station === ST_LM;
        const from = stationPan(run.station - 1);
        const to = stationPan(run.station);
        add(prevLeave, "hop", (at) => this.swoosh(at, this.real(pass.travel + pass.stagger * (n - 1)), from, to, 0.05));
        pass.tokens.forEach((token, k) => {
          if (lm && k !== n - 1) return;
          add(prevLeave + (lm ? 0 : k * pass.stagger) + pass.travel, "arrive", (at) =>
            this.plink(at, note(token), to, n > 1 ? 0.05 : 0.08)
          );
        });
        prevLeave = run.leave;
      }
      if (n > 1) add(pass.retire, "retire", (at) => this.dissolve(at, stationPan(5)));

      for (const run of pass.runs) {
        const pan = stationPan(run.station);
        const inside = run.station === ST_L1 && I;
        // Weight packets arriving from memory
        if (run.station !== ST_EMBED)
          run.packets.forEach((e, k) =>
            add(e + pt, "packet", (at) =>
              this.tick(at, 1500 + run.station * 140 + hash(e, k) * 300, pass.kind === "decode" ? 0.028 : 0.02, pan)
            )
          );
        // A box starting heavy math clunks into gear (the chatter does the rest)
        if (run.compute && run.compute.heat >= 0.9 && !inside)
          add(run.compute.start, "engage", (at) => this.engage(at, pan));
        // Cache writes
        const lastWrite = run.kvWrites.length ? Math.max(...run.kvWrites.map((w) => w.leave)) : 0;
        if (!inside)
          for (const w of run.kvWrites) {
            if (model === "linear" && w.leave !== lastWrite) continue;
            add(w.leave + pt, "kv-write", (at) => this.chk(at, model === "linear" ? 76 : note(w.token), pan));
          }
        // Cache reads: every earlier token, one note each, so the pile gets longer to hear
        if (model === "linear" && run.kvReads.length) {
          const first = run.kvReads[0].leave;
          add(first, "kv-read", (at) => this.readNote(at, 64, pan));
          add(first + 0.12 * pt, "kv-read", (at) => this.readNote(at, 71, pan));
        } else
          for (const r of model === "linear" ? [] : run.kvReads)
            add(r.leave, "kv-read", (at) => this.readNote(at, note(r.token), pan));
        // Layers 4 to 89 flash past on the counter
        if (run.station === ST_WARP) {
          const dur = run.leave - run.arrive;
          const every = Math.max(1, Math.ceil(85 / (dur * 38)));
          add(run.arrive, "warp", (at) => this.warp(at, this.real(dur)));
          for (let v = 5; v <= 89; v += every) {
            const f = (v - 4) / 86;
            add(run.arrive + f * dur, "layer", (at) => this.layerTick(at, f, pan));
          }
        }
      }

      if (I) this.interiorCues(add, I);

      // The LM head: one vector sprays into a score for every token, the scores collapse, the winner pops out
      const size = pass.kind === "prefill" ? 1 : pass.index === 1 ? 0.9 : 0.8;
      const out = note(pass.out);
      add(pass.burst, "spray", (at) => this.spray(at, this.real(0.6 * s)));
      const riseFrom = pass.kind === "prefill" ? pass.scoresIn : pass.burst + 0.3 * s;
      add(riseFrom, "riser", (at) => this.riser(at, this.real(pass.smash - riseFrom), size));
      add(pass.converge, "vacuum", (at) => this.vacuum(at, this.real(pass.smash - pass.converge), size));
      add(pass.smash, "smash", (at) => this.impact(at, size, out));
      add(pass.pop, "pop", (at) => this.bubble(at, out + 12, 0.6, 0.16));
      const flyStart = pass.pop + 0.25 * s;
      add(flyStart, "fly", (at) => this.swoosh(at, this.real(pass.land - flyStart), 0.6, -0.2, 0.07));
      add(pass.land, "land", (at) => this.landChime(at, out));
    }

    add(this.outroStart, "outro", (at) => this.outro(at));
    add(tl.endCard + 0.3, "row", (at) => this.engage(at, -0.2));
    for (let k = 0; k < 8; k++)
      add(tl.endCard + 0.8 + k * 0.06, "row", (at) => this.tick(at, 2200 + k * 90, 0.04, 0.2));

    this.cues = cues.sort((a, b) => a.t - b.t);
  }

  /** Zoomed into layer 1: attention, the KV cache, experts or the feed-forward block. */
  private interiorCues(add: (t: number, kind: string, play: (at: number) => void) => void, I: Pass["interior"]) {
    const model = this.model;
    const lane = (i: number) => -0.5 + i * 0.12;
    add(I.zoomIn[0], "zoom", (at) => this.zoom(at, this.real(I.zoomIn[1] - I.zoomIn[0]), true));
    add(I.zoomOut[0], "zoom", (at) => this.zoom(at, this.real(I.zoomOut[1] - I.zoomOut[0]), false));
    add(I.enter, "hop", (at) => this.swoosh(at, this.real(0.9), -0.8, -0.3, 0.05));
    for (let i = 0; i < PROMPT_COUNT; i++)
      add(I.enter + i * 0.05 + 0.9, "arrive", (at) => this.plink(at, note(i), -0.3, 0.045));
    I.attnPackets.forEach((e, k) =>
      add(e + 0.35, "packet", (at) => this.tick(at, 1700 + hash(e, k) * 400, 0.022, -0.1))
    );

    if (model === "linear") {
      for (let i = 0; i < PROMPT_COUNT; i++) {
        const fold = I.foldLaunch[i] + I.foldTravel;
        add(fold, "fold", (at) => this.gulp(at, note(i), lane(i)));
        add(fold + 0.05 + I.readTravel, "read", (at) => this.shimmer(at, note(i), lane(i)));
      }
      add(I.stateStore + I.kvTravel, "store", (at) => this.store(at));
    } else {
      for (let i = 0; i < PROMPT_COUNT; i++)
        add(I.kvLeave[i] + I.kvTravel, "kv-write", (at) => this.chk(at, note(i), -0.4 + i * 0.03));
      for (let i = 1; i < PROMPT_COUNT; i++) {
        // The query pings, then everything it attended to arrives as a strum, loudest where attention was strongest
        add(I.mixLaunch[i] - 0.3, "ping", (at) => this.sonar(at, note(i), lane(i)));
        add(I.mixLaunch[i] + I.mixTravel, "mix", (at) => this.merge(at, i, lane(i)));
      }
    }

    add(I.toRouter, "hop", (at) => this.swoosh(at, this.real(0.6), -0.2, 0.05, 0.045));
    const merge = (i: number) => I.expertOut[i] + I.returnTravel;
    const packetTime = model === "moe" ? 0.22 : 0.35;
    I.expertPackets.forEach((e, k) =>
      add(e + packetTime, "packet", (at) => this.tick(at, 2000 + hash(e, k, 1) * 500, 0.02, 0.3))
    );
    for (let i = 0; i < PROMPT_COUNT; i++) {
      if (model === "moe") {
        const [a, b] = ROUTES[i];
        add(I.dispatch[i], "dispatch", (at) => {
          this.zap(at, EXPERT_NOTES[a], -0.1 + a * 0.1);
          this.zap(at + 0.03, EXPERT_NOTES[b], -0.1 + b * 0.1);
        });
      } else add(I.dispatch[i], "expand", (at) => this.vwoop(at, true, 0.1));
      add(merge(i), "merge", (at) => {
        if (model !== "moe") this.vwoop(Math.max(0, at - 0.05), false, 0.6);
        this.thump(at, note(i), 0.7);
      });
    }
    add(I.exit, "hop", (at) => this.swoosh(at, this.real(0.6), 0.4, 0.9, 0.05));
  }

  // ---------------------------------------------------------------------------
  // Mix automation

  private mixAt(t: number, at: number, deadman: boolean) {
    // Inside the zoomed layer the music steps back a little, so the detail can be heard.
    const I = this.tl.passes[0].interior;
    let inside = 0;
    if (t >= I.zoomIn[0] && t <= I.zoomOut[1]) {
      if (t < I.zoomIn[1]) inside = (t - I.zoomIn[0]) / (I.zoomIn[1] - I.zoomIn[0]);
      else if (t < I.zoomOut[0]) inside = 1;
      else inside = 1 - (t - I.zoomOut[0]) / (I.zoomOut[1] - I.zoomOut[0]);
    }
    const slow = this.speed < 1;
    if (deadman) this.musicTone.frequency.cancelScheduledValues(at);
    this.musicTone.frequency.setTargetAtTime(Math.min(slow ? 2200 : 18000, lerp(18000, 7000, inside)), at, 0.1);
    if (deadman) this.musicLevel.gain.cancelScheduledValues(at);
    this.musicLevel.gain.setTargetAtTime(1 - 0.15 * inside, at, 0.1);
  }

  // ---------------------------------------------------------------------------
  // Building blocks

  private gainNode(v: number) {
    const g = this.ctx.createGain();
    g.gain.value = v;
    return g;
  }

  private filterNode(type: BiquadFilterType, freq: number, q = 0.7) {
    const f = this.ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq;
    f.Q.value = q;
    return f;
  }

  /** The input of a voice's output chain: pan, then the bus, plus reverb and echo sends. */
  private out(bus: Bus | GainNode, opts: { pan?: number; verb?: number; echo?: number } = {}): GainNode {
    const input = this.gainNode(1);
    const chain: AudioNode[] = [];
    let node: AudioNode = input;
    if (opts.pan) {
      const p = this.ctx.createStereoPanner();
      p.pan.value = clamp(opts.pan, -1, 1);
      input.connect(p);
      node = p;
      chain.push(p);
    }
    node.connect(typeof bus === "string" ? this.epoch[bus] : bus);
    for (const [amount, to] of [
      [opts.verb, this.epoch.verb],
      [opts.echo, this.epoch.echo]
    ] as Array<[number | undefined, GainNode]>) {
      if (!amount) continue;
      const send = this.gainNode(amount);
      node.connect(send).connect(to);
      chain.push(send);
    }
    this.chains.set(input, chain);
    return input;
  }

  private osc(type: OscillatorType | "pulse", freq: number, at: number, stop: number) {
    const o = this.ctx.createOscillator();
    if (type === "pulse") o.setPeriodicWave(this.pulse);
    else o.type = type;
    o.frequency.setValueAtTime(freq, at);
    o.start(at);
    o.stop(stop);
    return o;
  }

  private noiseSrc(at: number, stop: number) {
    const s = this.ctx.createBufferSource();
    s.buffer = this.noise;
    s.loop = true;
    s.start(at, Math.random() * 1.8);
    s.stop(stop);
    return s;
  }

  /** A percussive envelope: near-instant attack, exponential decay. */
  private perc(at: number, peak: number, decay: number, attack = 0.003) {
    const g = this.gainNode(0);
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), at + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, at + attack + decay);
    return g;
  }

  /** Disconnects a voice's nodes once its source ends. */
  private cleanup(src: AudioScheduledSourceNode, nodes: AudioNode[]) {
    src.onended = () =>
      nodes.forEach((n) => {
        n.disconnect();
        this.chains.get(n)?.forEach((c) => c.disconnect());
      });
  }

  private blip(
    at: number,
    freq: number,
    decay: number,
    opts: {
      type?: OscillatorType | "pulse";
      vol?: number;
      glide?: number;
      glideTime?: number;
      pan?: number;
      verb?: number;
      echo?: number;
      dest?: Bus | GainNode;
    } = {}
  ) {
    const { type = "sine", vol = 0.1, glide, glideTime = decay, dest = "sfx" } = opts;
    const o = this.osc(type, freq, at, at + decay + 0.05);
    if (glide) o.frequency.exponentialRampToValueAtTime(glide, at + glideTime);
    const g = this.perc(at, vol, decay);
    const outNode = this.out(dest, opts);
    o.connect(g).connect(outNode);
    this.cleanup(o, [o, g, outNode]);
  }

  private noiseHit(
    at: number,
    decay: number,
    vol: number,
    filter: { type: BiquadFilterType; freq: number; q?: number; to?: number },
    opts: { pan?: number; verb?: number; dest?: Bus | GainNode; attack?: number } = {}
  ) {
    const src = this.noiseSrc(at, at + (opts.attack ?? 0.003) + decay + 0.05);
    const f = this.filterNode(filter.type, filter.freq, filter.q ?? 0.7);
    if (filter.to) f.frequency.exponentialRampToValueAtTime(filter.to, at + decay);
    const g = this.perc(at, vol, decay, opts.attack);
    const outNode = this.out(opts.dest ?? "sfx", opts);
    src.connect(f).connect(g).connect(outNode);
    this.cleanup(src, [src, f, g, outNode]);
  }

  /** Noise swept through a band-pass, with an optional pan sweep: whooshes, zooms, risers. */
  private sweep(
    at: number,
    dur: number,
    f0: number,
    f1: number,
    vol: number,
    opts: { q?: number; pan0?: number; pan1?: number; shape?: "swell" | "rise" | "fall"; verb?: number } = {}
  ) {
    const { q = 1.4, pan0 = 0, pan1 = pan0, shape = "swell" } = opts;
    const end = at + dur;
    const src = this.noiseSrc(at, end + 0.05);
    const f = this.filterNode("bandpass", f0, q);
    f.frequency.setValueAtTime(f0, at);
    f.frequency.exponentialRampToValueAtTime(f1, end);
    const g = this.gainNode(0);
    g.gain.setValueAtTime(0.0001, at);
    if (shape === "rise") {
      g.gain.exponentialRampToValueAtTime(vol, end - 0.005);
      g.gain.linearRampToValueAtTime(0, end);
    } else if (shape === "fall") {
      g.gain.exponentialRampToValueAtTime(vol, at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, end);
    } else {
      g.gain.exponentialRampToValueAtTime(vol, at + dur * 0.55);
      g.gain.exponentialRampToValueAtTime(0.0001, end);
    }
    const p = this.ctx.createStereoPanner();
    p.pan.setValueAtTime(clamp(pan0, -1, 1), at);
    p.pan.linearRampToValueAtTime(clamp(pan1, -1, 1), end);
    src.connect(f).connect(g).connect(p).connect(this.epoch.sfx);
    const send = this.gainNode(opts.verb ?? 0);
    if (opts.verb) p.connect(send).connect(this.epoch.verb);
    this.cleanup(src, [src, f, g, p, send]);
  }

  // ---------------------------------------------------------------------------
  // Instruments

  /** A 909-style kick: a fast pitch drop into a round thump, with a click on top. */
  private kick(at: number, vel: number, pump = true) {
    const o = this.osc("sine", 190, at, at + 0.45);
    o.frequency.exponentialRampToValueAtTime(55, at + 0.055);
    o.frequency.exponentialRampToValueAtTime(44, at + 0.3);
    const g = this.perc(at, 0.9 * vel, 0.34, 0.002);
    const dest = this.out("drums");
    o.connect(g).connect(dest);
    this.cleanup(o, [o, g, dest]);
    this.noiseHit(at, 0.008, 0.26 * vel, { type: "highpass", freq: 3000 }, { dest: "drums" });
    if (pump) {
      // Sidechain: the pads and bass breathe around every kick.
      this.pump.gain.setTargetAtTime(0.35, at, 0.004);
      this.pump.gain.setTargetAtTime(1, at + 0.05, this.real(0.09));
    }
  }

  private snare(at: number, vel: number) {
    this.noiseHit(at, 0.16, 0.34 * vel, { type: "bandpass", freq: 1900, q: 0.8 }, { dest: "drums", verb: 0.25 });
    this.blip(at, 210, 0.08, { type: "triangle", vol: 0.24 * vel, glide: 150, dest: "drums" });
  }

  private hat(at: number, vel: number, open = false) {
    this.noiseHit(
      at,
      open ? 0.2 : 0.035,
      (open ? 0.085 : 0.1) * vel,
      { type: "highpass", freq: open ? 7000 : 8000 },
      {
        dest: "drums",
        pan: 0.25
      }
    );
  }

  /** A drum-machine clap: three quick bursts and a short tail. */
  private clap(at: number, vel: number) {
    [0, 0.011, 0.022].forEach((d, i) =>
      this.noiseHit(at + d, 0.012, (0.24 - i * 0.03) * vel, { type: "bandpass", freq: 1200, q: 1.1 }, { dest: "drums" })
    );
    this.noiseHit(at + 0.03, 0.16, 0.16 * vel, { type: "bandpass", freq: 1100, q: 0.9 }, { dest: "drums", verb: 0.35 });
  }

  /** Toms for the fills, `i` from 3 (high) down to 0 (low), panned across. */
  private tom(at: number, i: number) {
    const f = [92, 118, 148, 185][i];
    this.blip(at, f * 1.6, 0.24, {
      vol: 0.34,
      glide: f,
      glideTime: 0.12,
      pan: -0.45 + i * 0.3,
      dest: "drums",
      verb: 0.2
    });
    this.noiseHit(at, 0.02, 0.05, { type: "bandpass", freq: 2500, q: 1 }, { dest: "drums", pan: -0.45 + i * 0.3 });
  }

  private crash(at: number, vel: number) {
    this.noiseHit(at, 1.6, 0.075 * vel, { type: "highpass", freq: 5500 }, { dest: "drums", pan: -0.3, verb: 0.3 });
    this.noiseHit(at, 1.4, 0.075 * vel, { type: "highpass", freq: 6500 }, { dest: "drums", pan: 0.3, verb: 0.3 });
  }

  private bass(at: number, midi: number, dur: number, vel: number) {
    const end = at + dur;
    const a = this.osc("sawtooth", hz(midi), at, end + 0.08);
    const b = this.osc("sine", hz(midi - 12), at, end + 0.08);
    const f = this.filterNode("lowpass", 180, 7);
    f.frequency.setValueAtTime(180, at);
    f.frequency.exponentialRampToValueAtTime(320 + 1100 * vel, at + 0.012);
    f.frequency.exponentialRampToValueAtTime(220, at + Math.max(0.05, dur * 0.9));
    const g = this.gainNode(0);
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(0.16 * vel, at + 0.005);
    g.gain.setValueAtTime(0.16 * vel, end);
    g.gain.linearRampToValueAtTime(0, end + 0.05);
    const sub = this.gainNode(0.9);
    const dest = this.out("pumped");
    a.connect(f);
    b.connect(sub).connect(f);
    f.connect(g).connect(dest);
    this.cleanup(a, [a, b, sub, f, g, dest]);
  }

  /** An FM "slap" bass, the Streets of Rage sound: bright on the attack, round after. */
  private fmBass(at: number, midi: number, dur: number, vel: number) {
    const f = hz(midi);
    const end = at + dur;
    const car = this.osc("sine", f, at, end + 0.06);
    const mod = this.osc("sine", f, at, end + 0.06);
    const idx = this.gainNode(0);
    idx.gain.setValueAtTime(f * 4.5 * vel, at);
    idx.gain.exponentialRampToValueAtTime(f * 0.5, at + 0.09);
    mod.connect(idx).connect(car.frequency);
    const g = this.gainNode(0);
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(0.085 * vel, at + 0.004);
    g.gain.setTargetAtTime(0.05 * vel, at + 0.004, 0.08);
    g.gain.setValueAtTime(0.05 * vel, end);
    g.gain.linearRampToValueAtTime(0, end + 0.04);
    const dest = this.out("pumped");
    car.connect(g).connect(dest);
    this.cleanup(car, [car, mod, idx, g, dest]);
  }

  /** Gated saw chord stabs, Tron style, with an echo trailing off the offbeats. */
  private stab(at: number, notes: number[], vel: number, pan: number) {
    const f = this.filterNode("lowpass", 5200, 2);
    f.frequency.setValueAtTime(5200, at);
    f.frequency.exponentialRampToValueAtTime(900, at + 0.09);
    const g = this.perc(at, 0.042 * vel, 0.14);
    const dest = this.out("pumped", { pan, echo: 0.2, verb: 0.15 });
    f.connect(g).connect(dest);
    const oscs = notes.flatMap((m) =>
      [-7, 7].map((cents) => {
        const o = this.osc("sawtooth", hz(m + 12), at, at + 0.2);
        o.detune.value = cents;
        o.connect(f);
        return o;
      })
    );
    this.cleanup(oscs[0], [...oscs, f, g, dest]);
  }

  private pad(at: number, notes: number[], dur: number, vol: number, attack = 0.3) {
    const end = at + dur;
    const release = 0.7;
    const f = this.filterNode("lowpass", 1300, 0.6);
    const g = this.gainNode(0);
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(vol, at + Math.min(attack, dur * 0.5));
    g.gain.setValueAtTime(vol, end);
    g.gain.setTargetAtTime(0, end, release / 3);
    const dest = this.out("pumped", { verb: 0.45 });
    f.connect(g).connect(dest);
    const oscs: OscillatorNode[] = [];
    notes.forEach((m, i) => {
      for (const cents of [-8, 8]) {
        const o = this.osc("sawtooth", hz(m), at, end + release + 0.1);
        o.detune.value = cents + (i % 2 ? 3 : -3);
        o.connect(f);
        oscs.push(o);
      }
    });
    this.cleanup(oscs[0], [...oscs, f, g, dest]);
  }

  /**
   * The arpeggio, whose timbre follows the model: chiptune pulse for MoE, saw pluck for dense, a bell for linear.
   * `sweep` (0..1) opens the filter, or brightens the bell.
   */
  private arp(at: number, midi: number, vel: number, pan: number, echo = 0.28, sweep = 0.5) {
    const freq = hz(midi);
    const dest = this.out("pumped", { pan, echo });
    if (this.model === "linear") {
      const car = this.osc("sine", freq, at, at + 0.45);
      const mod = this.osc("sine", freq * 3.5, at, at + 0.45);
      const idx = this.gainNode(0);
      idx.gain.setValueAtTime(freq * (1.2 + 2 * sweep), at);
      idx.gain.exponentialRampToValueAtTime(freq * 0.15, at + 0.25);
      mod.connect(idx).connect(car.frequency);
      const g = this.perc(at, 0.075 * vel, 0.38);
      car.connect(g).connect(dest);
      this.cleanup(car, [car, mod, idx, g, dest]);
    } else if (this.model === "dense") {
      const o = this.osc("sawtooth", freq, at, at + 0.25);
      const top = 1400 + 4200 * sweep;
      const f = this.filterNode("lowpass", top, 6);
      f.frequency.setValueAtTime(top, at);
      f.frequency.exponentialRampToValueAtTime(600, at + 0.12);
      const g = this.perc(at, 0.06 * vel, 0.18);
      o.connect(f).connect(g).connect(dest);
      this.cleanup(o, [o, f, g, dest]);
    } else {
      const o = this.osc("pulse", freq, at, at + 0.2);
      const f = this.filterNode("lowpass", 1300 + 4000 * sweep, 5);
      const g = this.perc(at, 0.055 * vel, 0.13);
      o.connect(f).connect(g).connect(dest);
      this.cleanup(o, [o, f, g, dest]);
    }
  }

  /** A brassy saw lead that glides from the note before when they touch, with vibrato on held notes. */
  private lead(at: number, midi: number, dur: number, vol = 0.055) {
    const end = at + dur;
    const from = this.lastLead && at - this.lastLead.end < 0.06 ? this.lastLead.midi : midi;
    this.lastLead = { midi, end };
    const lfo = this.osc("sine", 5.8, at, end + 0.2);
    const depth = this.gainNode(0);
    depth.gain.setValueAtTime(0, at);
    depth.gain.setValueAtTime(0, at + Math.min(0.15, dur * 0.5));
    depth.gain.linearRampToValueAtTime(16, at + Math.min(0.35, dur));
    lfo.connect(depth);
    const f = this.filterNode("lowpass", 700, 2);
    f.frequency.setValueAtTime(700, at);
    f.frequency.exponentialRampToValueAtTime(3600, at + 0.05);
    f.frequency.setTargetAtTime(2300, at + 0.05, 0.12);
    const g = this.gainNode(0);
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(vol, at + 0.015);
    g.gain.setValueAtTime(vol * 0.85, end);
    g.gain.setTargetAtTime(0, end, 0.04);
    const dest = this.out("music", { verb: 0.28, echo: 0.2, pan: 0.1 });
    const voices: Array<[OscillatorType | "pulse", number, number]> = [
      ["sawtooth", -7, 0],
      ["sawtooth", 7, 0],
      ["pulse", 0, -12]
    ];
    const oscs = voices.map(([type, cents, shift]) => {
      const o = this.osc(type, hz(from + shift), at, end + 0.2);
      if (from !== midi) o.frequency.exponentialRampToValueAtTime(hz(midi + shift), at + 0.045);
      o.detune.value = cents;
      depth.connect(o.detune);
      o.connect(f);
      return o;
    });
    f.connect(g).connect(dest);
    this.cleanup(oscs[0], [...oscs, lfo, depth, f, g, dest]);
  }

  // ---------------------------------------------------------------------------
  // Sound effects

  private keyClick(at: number, space: boolean, pan: number) {
    this.noiseHit(
      at,
      space ? 0.04 : 0.018,
      space ? 0.16 : 0.12,
      {
        type: "bandpass",
        freq: space ? 900 : 2600 + Math.random() * 1600,
        q: 3
      },
      { pan }
    );
    if (!space) this.blip(at, 1700 + Math.random() * 500, 0.012, { type: "square", vol: 0.018, pan });
  }

  private laser(at: number, dur: number) {
    const o = this.osc("sawtooth", 160, at, at + dur + 0.05);
    o.frequency.exponentialRampToValueAtTime(1800, at + dur);
    const f = this.filterNode("bandpass", 400, 3);
    f.frequency.setValueAtTime(400, at);
    f.frequency.exponentialRampToValueAtTime(5000, at + dur);
    const g = this.gainNode(0);
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(0.07, at + 0.05);
    g.gain.setValueAtTime(0.07, at + dur - 0.08);
    g.gain.linearRampToValueAtTime(0, at + dur);
    const p = this.ctx.createStereoPanner();
    p.pan.setValueAtTime(-0.7, at);
    p.pan.linearRampToValueAtTime(0.3, at + dur);
    const send = this.gainNode(0.2);
    o.connect(f).connect(g).connect(p).connect(this.epoch.sfx);
    p.connect(send).connect(this.epoch.verb);
    this.cleanup(o, [o, f, g, p, send]);
  }

  private snip(at: number, pan: number) {
    this.noiseHit(at, 0.025, 0.14, { type: "highpass", freq: 3000 }, { pan });
    this.blip(at, 1400, 0.04, { vol: 0.07, glide: 500, pan });
  }

  /** A bubble pop: a sine that bends up into its note. */
  private bubble(at: number, midi: number, pan: number, vol = 0.12) {
    this.blip(at, hz(midi) * 0.75, 0.12, { vol, glide: hz(midi), glideTime: 0.04, pan, verb: 0.15 });
    this.blip(at + 0.01, hz(midi + 12), 0.07, { type: "triangle", vol: vol * 0.3, pan });
  }

  private ding(at: number, midi: number, pan: number) {
    this.blip(at, hz(midi + 12), 0.22, { vol: 0.05, pan, verb: 0.2 });
    this.blip(at, hz(midi + 24), 0.12, { vol: 0.02, pan });
  }

  private stinger(at: number, kind: "prefill" | "decode") {
    const hit = at + this.real(BANNER_HIT);
    // A swell into the slam, then a bright chord and a sub drop
    this.sweep(at, hit - at, 500, 5000, 0.08, { shape: "rise" });
    const chord = kind === "prefill" ? [57, 60, 64, 69] : [69, 72, 76, 81];
    const f = this.filterNode("lowpass", 6000, 1);
    f.frequency.setValueAtTime(6000, hit);
    f.frequency.exponentialRampToValueAtTime(900, hit + 0.7);
    const g = this.perc(hit, 0.1, 0.9);
    const dest = this.out("sfx", { verb: 0.45, echo: 0.15 });
    f.connect(g).connect(dest);
    const oscs = chord.flatMap((m) =>
      [-9, 9].map((cents) => {
        const o = this.osc("sawtooth", hz(m), hit, hit + 1);
        o.detune.value = cents;
        o.connect(f);
        return o;
      })
    );
    this.cleanup(oscs[0], [...oscs, f, g, dest]);
    this.blip(hit, 75, 0.5, { vol: 0.3, glide: 32 });
  }

  private drop(at: number, midi: number, dur: number) {
    this.blip(at, hz(midi + 12), dur, { type: "triangle", vol: 0.035, glide: hz(midi - 5), pan: -0.4 });
  }

  private lookup(at: number, midi: number) {
    this.blip(at, 190, 0.1, { vol: 0.18, glide: 85, pan: -0.7 });
    this.blip(at, hz(midi), 0.08, { type: "pulse", vol: 0.035, pan: -0.7 });
  }

  private tick(at: number, freq: number, vol: number, pan: number) {
    this.blip(at, freq, 0.016, { vol, pan });
  }

  private swoosh(at: number, dur: number, pan0: number, pan1: number, vol: number) {
    this.sweep(at, Math.max(0.12, dur), 700, 2600, vol, { pan0, pan1 });
  }

  private plink(at: number, midi: number, pan: number, vol: number) {
    this.blip(at, hz(midi + 12), 0.1, { vol, pan, echo: 0.12 });
    this.blip(at, hz(midi), 0.05, { type: "pulse", vol: vol * 0.35, pan });
  }

  private dissolve(at: number, pan: number) {
    for (let k = 0; k < 6; k++) this.blip(at + k * 0.045, hz(88 - k * 3), 0.06, { vol: 0.025, pan, verb: 0.3 });
  }

  private chk(at: number, midi: number, pan: number) {
    this.noiseHit(at, 0.018, 0.07, { type: "bandpass", freq: 3600, q: 5 }, { pan });
    this.blip(at, hz(midi + 24), 0.035, { vol: 0.014, pan });
  }

  private readNote(at: number, midi: number, pan: number) {
    this.blip(at, hz(midi + 12), 0.07, { type: "triangle", vol: 0.04, pan });
  }

  /** Heavy math starting up: a relay clunk. */
  private engage(at: number, pan: number) {
    this.blip(at, 95, 0.16, { vol: 0.28, glide: 48, pan });
    this.noiseHit(at, 0.09, 0.07, { type: "lowpass", freq: 500 }, { pan });
  }

  /** Layers 4 to 89: hyperdrive. */
  private warp(at: number, dur: number) {
    this.sweep(at, dur, 300, 3200, 0.05, { q: 2, pan0: -0.2, pan1: 0.2 });
    const o = this.osc("sawtooth", 55, at, at + dur + 0.05);
    o.frequency.exponentialRampToValueAtTime(220, at + dur);
    const f = this.filterNode("lowpass", 700, 2);
    const g = this.gainNode(0);
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(0.035, at + dur * 0.3);
    g.gain.linearRampToValueAtTime(0, at + dur);
    const dest = this.out("sfx");
    o.connect(f).connect(g).connect(dest);
    this.cleanup(o, [o, f, g, dest]);
  }

  private layerTick(at: number, f: number, pan: number) {
    this.blip(at, hz(57 + f * 36), 0.022, { type: "pulse", vol: 0.022, pan });
  }

  /** The LM head's burst: one vector becomes a score for every token in the vocabulary. */
  private spray(at: number, dur: number) {
    this.noiseHit(at, 0.3, 0.07, { type: "highpass", freq: 5000 }, { pan: 0.5, verb: 0.3 });
    for (let k = 0; k < 22; k++) {
      const when = at + dur * Math.pow(k / 22, 1.6);
      const m = [84, 86, 88, 91, 93, 96, 98, 100][Math.floor(Math.random() * 8)];
      this.blip(when, hz(m), 0.05, { vol: 0.02, pan: 0.4 + Math.random() * 0.4 });
    }
  }

  private riser(at: number, dur: number, size: number) {
    if (dur <= 0.05) return;
    this.sweep(at, dur, 400, 7000, 0.1 * size, { q: 2.5, shape: "rise", pan0: 0.3, pan1: 0.6 });
    const o = this.osc("sawtooth", hz(45), at, at + dur);
    o.frequency.exponentialRampToValueAtTime(hz(69), at + dur);
    const f = this.filterNode("lowpass", 600, 3);
    f.frequency.exponentialRampToValueAtTime(5000, at + dur);
    const g = this.gainNode(0);
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(0.04 * size, at + dur - 0.01);
    g.gain.linearRampToValueAtTime(0, at + dur);
    const dest = this.out("sfx", { pan: 0.5 });
    o.connect(f).connect(g).connect(dest);
    this.cleanup(o, [o, f, g, dest]);
  }

  /** Every other candidate is sucked into the winner: a reverse cymbal. */
  private vacuum(at: number, dur: number, size: number) {
    if (dur <= 0.05) return;
    const src = this.noiseSrc(at, at + dur);
    const f = this.filterNode("highpass", 900, 0.7);
    f.frequency.exponentialRampToValueAtTime(4000, at + dur);
    const g = this.gainNode(0);
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(0.16 * size, at + dur - 0.004);
    g.gain.linearRampToValueAtTime(0, at + dur);
    const dest = this.out("sfx", { pan: 0.6 });
    src.connect(f).connect(g).connect(dest);
    this.cleanup(src, [src, f, g, dest]);
  }

  /** Compression: many candidates smash into one token. */
  private impact(at: number, size: number, midi: number) {
    this.kick(at, 1.1 * size, false);
    this.blip(at, 110, 1.1, { vol: 0.6 * size, glide: 28, glideTime: 0.7, dest: "sfx" });
    this.noiseHit(at, 1.4 * size, 0.3 * size, { type: "highpass", freq: 3500 }, { pan: -0.4, verb: 0.55 });
    this.noiseHit(at, 1.3 * size, 0.3 * size, { type: "highpass", freq: 4200 }, { pan: 0.4, verb: 0.55 });
    this.noiseHit(at, 0.35, 0.35 * size, { type: "bandpass", freq: 900, q: 0.8 }, { verb: 0.3 });
    // Crunch
    const o = this.osc("sawtooth", hz(midi - 36), at, at + 0.35);
    const shaper = this.ctx.createWaveShaper();
    shaper.curve = this.crunch;
    const f = this.filterNode("lowpass", 3200, 1);
    const g = this.perc(at, 0.07 * size, 0.25);
    const dest = this.out("sfx");
    o.connect(shaper).connect(f).connect(g).connect(dest);
    this.cleanup(o, [o, shaper, f, g, dest]);
    // Stab: the winner's note, fifth and octave
    const sf = this.filterNode("lowpass", 5500, 1);
    sf.frequency.setValueAtTime(5500, at);
    sf.frequency.exponentialRampToValueAtTime(700, at + 0.9);
    const sg = this.perc(at, 0.12 * size, 1.1);
    const sdest = this.out("sfx", { verb: 0.5, echo: 0.2 });
    sf.connect(sg).connect(sdest);
    const oscs = [midi - 12, midi - 5, midi].map((m) => {
      const s = this.osc("sawtooth", hz(m), at, at + 1.2);
      s.connect(sf);
      return s;
    });
    this.cleanup(oscs[0], [...oscs, sf, sg, sdest]);
    // The music ducks out of the way and swells back.
    this.duck.gain.setTargetAtTime(size >= 1 ? 0.2 : 0.6, at, 0.008);
    this.duck.gain.setTargetAtTime(1, at + (size >= 1 ? 0.25 : 0.1), size >= 1 ? 0.45 : 0.2);
  }

  private landChime(at: number, midi: number) {
    this.blip(at, hz(midi + 12), 0.6, { vol: 0.09, verb: 0.3, echo: 0.2, pan: -0.2 });
    this.blip(at, hz(midi + 19), 0.4, { vol: 0.035, verb: 0.3, pan: -0.2 });
    this.blip(at, hz(midi + 24), 0.3, { vol: 0.03, pan: -0.2 });
    this.blip(at + 0.03, hz(midi + 24), 0.05, { type: "pulse", vol: 0.03, pan: -0.2 });
    this.blip(at, 170, 0.1, { vol: 0.18, glide: 80, pan: -0.2 });
  }

  private fastForward(at: number) {
    for (const d of [0, 0.13]) this.blip(at + d, 320, 0.11, { type: "pulse", vol: 0.04, glide: 2400, glideTime: 0.1 });
    this.noiseHit(at, 0.25, 0.04, { type: "bandpass", freq: 2000, q: 1, to: 6000 });
  }

  private zoom(at: number, dur: number, into: boolean) {
    this.sweep(at, dur, into ? 3000 : 400, into ? 300 : 3000, 0.07, { q: 1.2, verb: 0.3 });
    this.blip(at, into ? 330 : 110, dur, { type: "triangle", vol: 0.05, glide: into ? 110 : 330 });
  }

  private sonar(at: number, midi: number, pan: number) {
    this.blip(at, hz(midi + 12), 0.5, { vol: 0.05, glide: hz(midi + 12) * 0.97, pan, verb: 0.5, echo: 0.25 });
  }

  /** Attention: everything the token looked at arrives as a strum, loudest where it attended most. */
  private merge(at: number, i: number, pan: number) {
    const sources = Array.from({ length: i }, (_, j) => j)
      .map((j) => ({ j, w: attentionWeight(i, j) }))
      .filter((s) => s.w >= 0.3);
    sources.forEach(({ j, w }, k) =>
      this.blip(at + k * 0.018, hz(note(j)), 0.22, { type: "triangle", vol: 0.05 * w, pan: pan - 0.2 })
    );
    this.thump(at + sources.length * 0.018, note(i), pan);
  }

  private thump(at: number, midi: number, pan: number) {
    this.blip(at, 130, 0.16, { vol: 0.22, glide: 60, pan });
    this.blip(at, hz(midi), 0.18, { type: "pulse", vol: 0.04, pan, echo: 0.15 });
  }

  private gulp(at: number, midi: number, pan: number) {
    this.blip(at, hz(midi + 12), 0.16, { vol: 0.08, glide: hz(midi), glideTime: 0.1, pan });
    this.blip(at, 110, 0.1, { vol: 0.12, glide: 70, pan });
  }

  private shimmer(at: number, midi: number, pan: number) {
    this.blip(at, hz(midi), 0.18, { vol: 0.05, glide: hz(midi + 12), glideTime: 0.14, pan, verb: 0.3 });
    this.blip(at, hz(midi + 19), 0.12, { type: "triangle", vol: 0.015, pan });
  }

  private store(at: number) {
    this.engage(at, -0.5);
    for (const [k, m] of [64, 69, 76].entries()) this.blip(at + k * 0.03, hz(m), 0.2, { type: "pulse", vol: 0.03 });
  }

  private zap(at: number, midi: number, pan: number) {
    this.blip(at, hz(midi), 0.08, { type: "pulse", vol: 0.045, glide: hz(midi + 7), glideTime: 0.05, pan, echo: 0.2 });
  }

  /** The feed-forward block widening each vector (up) and squeezing it back (down). */
  private vwoop(at: number, up: boolean, pan: number) {
    const o = this.osc("sawtooth", 110, at, at + 0.35);
    const f = this.filterNode("lowpass", up ? 300 : 3500, 7);
    f.frequency.exponentialRampToValueAtTime(up ? 3500 : 300, at + 0.25);
    const g = this.perc(at, 0.05, 0.3, 0.02);
    const dest = this.out("sfx", { pan });
    o.connect(f).connect(g).connect(dest);
    this.cleanup(o, [o, f, g, dest]);
  }

  /** Stage clear, kept short: a hit, a four-note brass fanfare in A major, a final hit, done. */
  private outro(at: number) {
    const S = this.stepLen;
    const r = (steps: number) => at + this.real(steps * S);
    this.crash(at, 1);
    this.kick(at, 1.1, false);
    this.stab(at, OUTRO_CHORD, 1.3, 0);
    this.bass(at, 45, this.real(S * 3), 0.9);
    this.kick(r(4), 0.9, false);
    this.bass(r(4), 45, this.real(S * 2), 0.8);
    const fanfare: Array<[number, number, number]> = [
      [0, 76, 1],
      [1.5, 81, 1],
      [3, 85, 1],
      [4, 88, 4]
    ];
    for (const [st, m, len] of fanfare) this.lead(r(st), m, this.real(len * S), 0.06);
    // The final hit rings briefly and stops.
    const end = r(8);
    this.crash(end, 0.8);
    this.kick(end, 1.1, false);
    this.stab(end, OUTRO_CHORD, 1.2, 0);
    this.pad(end, OUTRO_CHORD, this.real(0.5), 0.05, 0.02);
    this.bass(end, 33, this.real(S * 4), 0.7);
    for (let k = 0; k < 4; k++)
      this.blip(end + this.real(k * S), hz(81 + [4, 7, 12, 16][k]), 0.12, { vol: 0.02, echo: 0.15 });
  }

  // Controls

  private coin(at: number) {
    this.blip(at, 988, 0.08, { type: "pulse", vol: 0.1, dest: this.buses.sfx });
    this.blip(at + 0.075, 1319, 0.45, { type: "pulse", vol: 0.1, dest: this.buses.sfx, echo: 0 });
  }

  private select(at: number, model: ModelKind) {
    const base = { dense: 76, moe: 79, linear: 83 }[model];
    this.blip(at, hz(base), 0.07, { type: "pulse", vol: 0.07, dest: this.buses.sfx });
    this.blip(at + 0.06, hz(base + 5), 0.14, { type: "pulse", vol: 0.07, dest: this.buses.sfx });
  }

  private scrub(t: number) {
    const now = this.ctx.currentTime;
    if (now - this.lastScrub < 0.035) return;
    this.lastScrub = now;
    const f = t / this.tl.duration;
    this.blip(now + 0.003, 500 + 1500 * f, 0.022, { vol: 0.045, dest: this.buses.sfx });
  }

  private powerDown(at: number) {
    this.blip(at, 440, 0.3, { type: "square", vol: 0.04, glide: 55, dest: this.buses.sfx });
  }

  private powerUp(at: number) {
    this.blip(at, 110, 0.16, { type: "square", vol: 0.03, glide: 660, glideTime: 0.14, dest: this.buses.sfx });
  }

  private bulletTime(at: number, slow: boolean) {
    this.blip(at, slow ? 240 : 70, slow ? 0.7 : 0.35, {
      type: "triangle",
      vol: 0.12,
      glide: slow ? 60 : 260,
      dest: this.buses.sfx
    });
    const src = this.noiseSrc(at, at + 0.6);
    const f = this.filterNode("lowpass", slow ? 4000 : 400, 2);
    f.frequency.exponentialRampToValueAtTime(slow ? 300 : 4000, at + 0.5);
    const g = this.perc(at, 0.06, 0.5, 0.05);
    src.connect(f).connect(g).connect(this.buses.sfx);
    this.cleanup(src, [src, f, g]);
  }
}
